import React from "react";
import PropTypes from "prop-types";
import { Button, Card, CheckCircle, EmptyState } from "@opendate-app/setlist";
import { formatCents } from "../../GiftCardProducts/giftCardFormat";
import { whereItWorks } from "./GiftCardDetails";
import { productShape, teamShape } from "../giftCardPurchasePropTypes";

const firstWord = (name) => name.trim().split(/\s+/)[0];
const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

const GiftCardConfirmation = ({ confirmation, product, team, currency, upcomingShowsUrl, onBuyAnother }) => {
  const gift = Boolean(confirmation.recipientEmail);
  const giftTitle = gift ? "Your gift is on its way" : "Your gift card is on its way";
  const title = confirmation.processing ? "Your payment is processing" : giftTitle;
  const sendsOn = gift ? confirmation.deliveryLabel : null;
  const delivery = gift
    ? `We'll email ${firstWord(confirmation.recipientName)} the gift card with your message${sendsOn ? ` on ${sendsOn}` : ""}`
    : "We'll email the gift card to you";
  const description = confirmation.processing ? `${delivery} once your bank confirms the payment.` : `${delivery}.`;
  const rows = [
    confirmation.orderNumber && ["Order", `#${confirmation.orderNumber}`],
    confirmation.amountCents && [
      "Gift card",
      `${formatCents(confirmation.amountCents, currency)}${confirmation.quantity > 1 ? ` × ${confirmation.quantity}` : ""} · ${lowerFirst(whereItWorks(product, team.name))}`,
    ],
    confirmation.bonusCents && [
      "Bonus card",
      `${formatCents(confirmation.bonusCents, currency)}${confirmation.quantity > 1 ? ` × ${confirmation.quantity}` : ""} free`,
    ],
    gift && ["Going to", `${confirmation.recipientName} (${confirmation.recipientEmail})`],
    sendsOn && ["Sending on", sendsOn],
    confirmation.totalCents && ["Paid", formatCents(confirmation.totalCents, currency)],
    confirmation.email && ["Receipt to", confirmation.email],
  ].filter(Boolean);

  return (
    <div className="gift-card-purchase-confirmation" data-testid="gift-card-confirmation">
      <Card>
        <EmptyState icon={CheckCircle} title={title} description={description} />
        {rows.length > 0 && (
          <dl className="gift-card-purchase-confirmation-rows">
            {rows.map(([label, value]) => (
              <div key={label} className="gift-card-purchase-confirmation-row">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        )}
        <div className="gift-card-purchase-confirmation-actions">
          <Button variant="secondary" onClick={onBuyAnother}>
            Buy another
          </Button>
          <Button variant="primary" onClick={() => window.open(upcomingShowsUrl, "_blank", "noopener")}>
            See upcoming shows
          </Button>
        </div>
      </Card>
    </div>
  );
};

GiftCardConfirmation.propTypes = {
  confirmation: PropTypes.shape({
    orderNumber: PropTypes.number,
    amountCents: PropTypes.number,
    bonusCents: PropTypes.number,
    quantity: PropTypes.number,
    totalCents: PropTypes.number,
    recipientName: PropTypes.string,
    recipientEmail: PropTypes.string,
    deliveryLabel: PropTypes.string,
    email: PropTypes.string,
    processing: PropTypes.bool,
  }).isRequired,
  product: productShape.isRequired,
  team: teamShape.isRequired,
  currency: PropTypes.string.isRequired,
  upcomingShowsUrl: PropTypes.string.isRequired,
  onBuyAnother: PropTypes.func.isRequired,
};

export default GiftCardConfirmation;
